"use client";

import React, { useEffect, useRef, useState } from "react";

export default function LuxuryRedefinedPage() {
  const threeCanvasRef = useRef<HTMLCanvasElement>(null);
  const waveformRef = useRef<HTMLDivElement>(null);
  const mainContainerRef = useRef<HTMLDivElement>(null);
  const [hasStarted, setHasStarted] = useState(false);

  // ── STYLE (style.css converted to string) ──
  const css = `
    @import url('https://fonts.googleapis.com/css2?family=Cinzel:wght@400;500;700&family=Outfit:wght@300;400;500;700&display=swap');

    :root {
      --bg-primary: #050505;
      --bg-card: rgba(255,255,255,0.03);
      --border-color: rgba(255,255,255,0.07);
      --accent-gold: #d4af37;
      --accent-gold-glow: rgba(212,175,55,0.3);
      --text-active: #ffffff;
      --text-muted: rgba(255,255,255,0.25);
      --font-serif: "Cinzel", serif;
      --font-sans: "Outfit", sans-serif;
    }
    *{box-sizing:border-box;margin:0;padding:0;}
    html{scroll-behavior:auto;}
    body{background-color:var(--bg-primary);color:var(--text-active);font-family:var(--font-sans);height:100vh;overflow:hidden;}

    .slideshow-bg{position:fixed;top:0;left:0;width:100vw;height:100vh;z-index:-1;overflow:hidden;background-color:#030303;}
    .bg-image{position:absolute;top:0;left:0;width:100%;height:100%;object-fit:cover;opacity:0;filter:brightness(0.22) contrast(1.1) saturate(0.8) blur(3px);transform:scale(1.1);}

    .glow{position:fixed;border-radius:50%;filter:blur(150px);z-index:0;opacity:0.35;pointer-events:none;mix-blend-mode:screen;}
    .glow-1{width:400px;height:400px;background:radial-gradient(circle,var(--accent-gold-glow) 0%,transparent 70%);top:10%;left:15%;animation:floatGlow 12s infinite alternate ease-in-out;}
    .glow-2{width:500px;height:500px;background:radial-gradient(circle,rgba(255,255,255,0.05) 0%,transparent 70%);bottom:10%;right:15%;animation:floatGlow 18s infinite alternate-reverse ease-in-out;}
    @keyframes floatGlow{0%{transform:translate(0,0) scale(1);}100%{transform:translate(50px,30px) scale(1.1);}}

    .overlay{position:fixed;top:0;left:0;width:100%;height:100%;background:rgba(5,5,5,0.95);backdrop-filter:blur(10px);display:flex;justify-content:center;align-items:center;z-index:200;transition:opacity 1s cubic-bezier(0.25,1,0.5,1),visibility 1s;}
    .overlay.hidden{opacity:0;visibility:hidden;pointer-events:none;}

    .intro-content{text-align:center;max-width:600px;padding:2rem;z-index:201;}
    .sub-title{font-family:var(--font-sans);font-size:0.9rem;letter-spacing:0.4em;color:var(--accent-gold);margin-bottom:1rem;font-weight:500;opacity:0;transform:translateY(20px);animation:fadeUp 1.2s forwards 0.2s cubic-bezier(0.25,1,0.5,1);}
    .main-title{font-family:var(--font-serif);font-size:3.5rem;font-weight:400;letter-spacing:0.15em;margin-bottom:1.5rem;background:linear-gradient(135deg,#ffffff 0%,#a6a6a6 100%);background-clip:text;-webkit-text-fill-color:transparent;opacity:0;transform:translateY(25px);animation:fadeUp 1.4s forwards 0.4s cubic-bezier(0.25,1,0.5,1);}
    .intro-desc{font-size:1rem;color:var(--text-muted);margin-bottom:2.5rem;font-weight:300;letter-spacing:0.05em;opacity:0;transform:translateY(20px);animation:fadeUp 1.6s forwards 0.6s cubic-bezier(0.25,1,0.5,1);}
    @keyframes fadeUp{to{opacity:1;transform:translateY(0);}}

    .btn-primary{position:relative;background:transparent;border:1px solid rgba(255,255,255,0.15);color:#fff;padding:1.2rem 3rem;font-family:var(--font-sans);font-size:0.9rem;font-weight:500;letter-spacing:0.2em;text-transform:uppercase;cursor:pointer;overflow:hidden;transition:border-color 0.4s,color 0.4s;opacity:0;transform:translateY(20px);animation:fadeUp 1.8s forwards 0.8s cubic-bezier(0.25,1,0.5,1);}
    .btn-primary span{position:relative;z-index:2;}
    .btn-primary::before{content:"";position:absolute;top:0;left:-100%;width:100%;height:100%;background:linear-gradient(90deg,transparent,rgba(255,255,255,0.08),transparent);transition:left 0.6s;z-index:1;}
    .btn-primary:hover{border-color:var(--accent-gold);color:var(--accent-gold);box-shadow:0 0 25px var(--accent-gold-glow);}
    .btn-primary:hover::before{left:100%;}
    .btn-glow{display:none;}

    .main-container{width:100vw;height:100vh;overflow-y:auto;scroll-behavior:smooth;position:relative;z-index:10;opacity:0;transition:opacity 1.5s cubic-bezier(0.25,1,0.5,1);}
    .main-container.visible{opacity:1;}
    .main-container.hidden{display:none;}

    .header-fixed{position:fixed;top:0;left:0;width:100%;padding:1.8rem 5%;display:flex;justify-content:space-between;align-items:center;z-index:120;mix-blend-mode:difference;pointer-events:none;}
    .logo{font-family:var(--font-serif);font-size:0.95rem;letter-spacing:0.25em;font-weight:500;color:#fff;}
    .status{font-size:0.7rem;letter-spacing:0.15em;color:var(--accent-gold);text-transform:uppercase;display:flex;align-items:center;gap:0.5rem;}
    .status::before{content:"";display:inline-block;width:6px;height:6px;background-color:var(--accent-gold);border-radius:50%;box-shadow:0 0 8px var(--accent-gold);animation:pulse 1.5s infinite alternate;}
    @keyframes pulse{to{opacity:0.3;}}

    .editorial-section{width:100%;min-height:100vh;display:flex;align-items:center;position:relative;padding:7rem 8%;overflow:hidden;box-sizing:border-box;}
    .image-mask-container,.image-card-frame,.we-move-banner,.point-img,.image-frame-portrait{overflow:hidden;border-radius:6px;position:relative;background-color:rgba(255,255,255,0.02);border:1px solid var(--border-color);}
    .image-mask-container img,.image-card-frame img,.we-move-banner img,.point-img img,.image-frame-portrait img{width:100%;height:100%;object-fit:cover;display:block;}

    .sec-split{display:grid;grid-template-columns:1fr 1fr;gap:4rem;}
    .sec-left{display:flex;justify-content:center;align-items:center;}
    .sec-left .image-mask-container{width:100%;max-width:480px;height:60vh;}
    .sec-right{display:flex;flex-direction:column;justify-content:center;gap:2rem;}
    .title-overlap{position:relative;background-color:#a6a6a6;display:inline-block;}
    .three-js{position:absolute;top:0;left:0;width:100%;height:100%;z-index:-1;pointer-events:none;}
    .giant-text{font-family:var(--font-sans);font-size:5.5rem;font-weight:900;line-height:0.92;letter-spacing:-0.02em;text-transform:uppercase;}
    .small-overlap-img{position:absolute;bottom:-280px;right:50px;filter:grayscale();width:50%;z-index:-1;height:400px;box-shadow:0 15px 30px rgba(0,0,0,0.8);overflow:hidden;border-radius:6px;border:1px solid var(--border-color);}
    .small-overlap-img img{width:100%;height:100%;object-fit:cover;}
    .sub-desc{font-family:var(--font-serif);font-size:1.8rem;color:var(--text-muted);font-weight:300;line-height:1.4;}

    .sec-engine{display:grid;grid-template-columns:4.5fr 7.5fr;gap:5rem;}
    .engine-left{display:flex;flex-direction:column;justify-content:center;gap:2rem;}
    .section-title{font-family:var(--font-serif);font-size:2.2rem;font-weight:400;letter-spacing:0.02em;}
    .engine-left .image-card-frame{width:100%;height:40vh;}
    .engine-right{display:flex;flex-direction:column;justify-content:center;gap:1.5rem;}
    .cat-label{font-size:0.8rem;color:var(--accent-gold);letter-spacing:0.2em;text-transform:uppercase;padding-bottom:0.5rem;border-bottom:1px solid var(--border-color);display:inline-block;}
    .body-title-large{font-family:var(--font-sans);font-size:2.6rem;font-weight:600;line-height:1.25;letter-spacing:-0.01em;}
    .engine-columns{display:grid;grid-template-columns:1fr 1fr;gap:2rem;margin-top:1.5rem;font-size:0.9rem;color:var(--text-muted);line-height:1.7;}

    .sec-we-move{justify-content:center;}
    .we-move-container{width:100%;max-width:900px;position:relative;text-align:center;}
    .giant-text-outline{font-family:var(--font-sans);font-size:9rem;font-weight:900;color:transparent;-webkit-text-stroke:2px #fff;letter-spacing:0.05em;margin-bottom:-50px;position:relative;z-index:2;}
    .we-move-banner{width:100%;height:35vh;box-shadow:0 20px 50px rgba(0,0,0,0.6);}

    .sec-grid-points{justify-content:center;}
    .grid-points-container{display:grid;grid-template-columns:repeat(3,1fr);gap:3rem;width:100%;max-width:1100px;}
    .point-item{display:flex;flex-direction:column;gap:0.8rem;}
    .point-num{font-family:var(--font-serif);font-size:1.4rem;color:var(--accent-gold);}
    .point-item h3{font-size:1.5rem;font-weight:600;}
    .point-item p{font-size:0.88rem;color:var(--text-muted);line-height:1.6;}
    .point-img{width:100%;height:22vh;margin-top:auto;}

    .sec-banner-full{padding:0;justify-content:center;align-items:center;}
    .banner-full-bg{position:absolute;top:0;left:0;width:100%;height:100%;z-index:1;}
    .banner-full-bg::after{content:"";position:absolute;top:0;left:0;width:100%;height:100%;background:rgba(5,5,5,0.7);}
    .banner-full-bg img{width:100%;height:100%;object-fit:cover;}
    .banner-content{position:relative;z-index:2;text-align:center;max-width:800px;padding:2rem;}
    .banner-content h1{font-family:var(--font-sans);font-size:4.2rem;font-weight:800;letter-spacing:-0.01em;margin-bottom:1rem;}
    .sub-banner-title{font-family:var(--font-serif);font-size:1.8rem;color:var(--accent-gold);letter-spacing:0.1em;}

    .sec-timeless{display:grid;grid-template-columns:1fr 1fr;gap:4rem;}
    .timeless-left{display:flex;align-items:center;}
    .giant-headline{font-family:var(--font-sans);font-size:5rem;font-weight:900;line-height:1.05;letter-spacing:-0.02em;}
    .timeless-right{display:flex;flex-direction:column;justify-content:center;gap:2rem;}
    .image-frame-portrait{width:100%;max-width:380px;height:45vh;}
    .timeless-text{font-family:var(--font-serif);font-size:1.5rem;color:var(--text-muted);font-weight:300;line-height:1.4;}

    .sec-outro{justify-content:center;text-align:center;}
    .outro-content{max-width:650px;}
    .outro-subtitle{font-family:var(--font-sans);font-size:1rem;letter-spacing:0.5em;color:var(--accent-gold);margin-bottom:1rem;}
    .outro-title{font-family:var(--font-serif);font-size:4.5rem;font-weight:400;letter-spacing:0.12em;background:linear-gradient(135deg,#fff 0%,#a6a6a6 100%);-webkit-background-clip:text;-webkit-text-fill-color:transparent;}
    .signature-line{width:80px;height:1px;background-color:var(--accent-gold);margin:2.5rem auto 0;}

    .sticky-player{position:fixed;bottom:1.5rem;left:50%;transform:translateX(-50%) translateY(120px);width:92%;max-width:720px;background:rgba(8,8,8,0.8);border:1px solid var(--border-color);backdrop-filter:blur(30px);border-radius:18px;padding:1rem 1.5rem;display:flex;flex-direction:column;gap:0.8rem;box-shadow:0 25px 50px rgba(0,0,0,0.8);z-index:150;transition:transform 1s cubic-bezier(0.25,1,0.5,1);}
    .main-container.visible .sticky-player{transform:translateX(-50%) translateY(0);}
    .player-controls{display:flex;align-items:center;justify-content:space-between;width:100%;}
    .control-btn{background:rgba(255,255,255,0.04);border:1px solid var(--border-color);width:44px;height:44px;border-radius:50%;color:white;cursor:pointer;display:flex;align-items:center;justify-content:center;transition:background-color 0.3s,border-color 0.3s,transform 0.2s;}
    .control-btn:hover{background:rgba(255,255,255,0.08);border-color:var(--accent-gold);transform:scale(1.08);color:var(--accent-gold);}
    .control-btn svg{width:20px;height:20px;}
    .control-btn .hidden{display:none;}
    .time-display{font-size:0.8rem;letter-spacing:0.08em;color:var(--text-muted);}
    #time-current{color:var(--text-active);}
    #waveform{width:100%;height:42px;}

    @media(max-width:1024px){
      .editorial-section{padding:5rem 5%;}
      .sec-split,.sec-engine,.sec-timeless{grid-template-columns:1fr;gap:2rem;}
      .sec-left .image-mask-container,.engine-left .image-card-frame,.image-frame-portrait{height:30vh;max-width:100%;}
      .giant-text{font-size:3.5rem;}
      .body-title-large{font-size:1.8rem;}
      .giant-text-outline{font-size:5rem;}
      .grid-points-container{grid-template-columns:1fr;}
      .banner-content h1{font-size:2.5rem;}
      .giant-headline{font-size:3rem;}
      .outro-title{font-size:3rem;}
    }
  `;

  useEffect(() => {
    if (!hasStarted) return;

    // ── WAVESURFER + GSAP SCRIPT (script.js converted) ──
    const loadScript = (src: string): Promise<void> =>
      new Promise((resolve, reject) => {
        if (document.querySelector(`script[src="${src}"]`)) { resolve(); return; }
        const s = document.createElement("script");
        s.src = src;
        s.onload = () => resolve();
        s.onerror = reject;
        document.head.appendChild(s);
      });

    (async () => {
      await loadScript("https://unpkg.com/wavesurfer.js@7/dist/wavesurfer.min.js");
      await loadScript("https://cdnjs.cloudflare.com/ajax/libs/gsap/3.12.5/gsap.min.js");
      await loadScript("https://cdnjs.cloudflare.com/ajax/libs/gsap/3.12.5/ScrollToPlugin.min.js");

      const gsap = (window as any).gsap;
      const WaveSurfer = (window as any).WaveSurfer;
      gsap.registerPlugin((window as any).ScrollToPlugin);

      const captions = [
        { time: 0, text: "For centuries" },
        { time: 1.2, text: "greatness has never announced itself." },
        { time: 2.68, text: "It didn't need to." },
        { time: 4, text: "It was recognized." },
        { time: 6, text: "In the details." },
        { time: 7.5, text: "In the craftsmanship." },
        { time: 9, text: "In the pursuit of perfection." },
        { time: 11, text: "Today..." },
        { time: 12.5, text: "that legacy continues." },
        { time: 14, text: "More refined." },
        { time: 16, text: "More timeless." },
        { time: 18, text: "More extraordinary than ever before." },
        { time: 20, text: "This..." },
        { time: 21, text: "is luxury redefined." },
      ];

      const sectionMap = [
        { section: 0, captions: [0, 1] },
        { section: 1, captions: [2, 3] },
        { section: 2, captions: [4, 5] },
        { section: 3, captions: [6, 7] },
        { section: 4, captions: [8, 9] },
        { section: 5, captions: [10, 11] },
        { section: 6, captions: [12, 13] },
      ];

      const wavesurfer = WaveSurfer.create({
        container: "#waveform",
        waveColor: "rgba(255,255,255,0.15)",
        progressColor: "#d4af37",
        cursorColor: "transparent",
        barWidth: 2,
        barGap: 3,
        barRadius: 2,
        responsive: true,
        height: 42,
        url: "/models/assets/sound.mp3",
      });

      const mainContainer = mainContainerRef.current!;
      const playPauseBtn = document.getElementById("play-pause")!;
      const playIcon = playPauseBtn.querySelector(".play-icon") as HTMLElement;
      const pauseIcon = playPauseBtn.querySelector(".pause-icon") as HTMLElement;
      const timeCurrent = document.getElementById("time-current")!;
      const timeDuration = document.getElementById("time-duration")!;

      let activeSectionIndex = -1;
      let isScrolling = false;

      const formatTime = (s: number) => {
        const mins = Math.floor(s / 60);
        const secs = Math.floor(s % 60);
        return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
      };

      wavesurfer.on("ready", () => { timeDuration.textContent = formatTime(wavesurfer.getDuration()); });
      wavesurfer.on("audioprocess", (t: number) => { timeCurrent.textContent = formatTime(t); syncSections(t); });
      wavesurfer.on("seeking", (t: number) => { timeCurrent.textContent = formatTime(t); syncSections(t); });
      wavesurfer.on("finish", () => { playIcon.classList.remove("hidden"); pauseIcon.classList.add("hidden"); });

      function getSectionForTime(currentTime: number) {
        let captionIdx = -1;
        for (let i = captions.length - 1; i >= 0; i--) {
          if (currentTime >= captions[i].time) { captionIdx = i; break; }
        }
        if (captionIdx === -1) return { sectionIdx: -1, captionIdx: -1 };
        for (const mapping of sectionMap) {
          if (mapping.captions.includes(captionIdx)) return { sectionIdx: mapping.section, captionIdx };
        }
        return { sectionIdx: -1, captionIdx: -1 };
      }

      function syncSections(currentTime: number) {
        const { sectionIdx } = getSectionForTime(currentTime);
        if (sectionIdx !== activeSectionIndex && sectionIdx !== -1) {
          activeSectionIndex = sectionIdx;
          scrollToSection(sectionIdx);
          animateSection(sectionIdx);
        }
      }

      function scrollToSection(idx: number) {
        const el = document.getElementById(`section-${idx}`);
        if (!el || isScrolling) return;
        isScrolling = true;
        gsap.to(mainContainer, { scrollTo: { y: el, offsetY: 0 }, duration: 1.2, ease: "power3.inOut", onComplete: () => { isScrolling = false; } });
      }

      const animatedSections = new Set<number>();

      function splitTextToChars(element: HTMLElement) {
        const text = element.textContent || "";
        element.innerHTML = "";
        const chars: HTMLSpanElement[] = [];
        for (const char of text) {
          if (char === "\n") continue;
          const span = document.createElement("span");
          span.style.display = "inline-block";
          span.style.transformStyle = "preserve-3d";
          span.innerHTML = char === " " ? "&nbsp;" : char;
          element.appendChild(span);
          chars.push(span);
        }
        return chars;
      }

      function splitTextToWords(element: HTMLElement) {
        const text = element.textContent || "";
        element.innerHTML = "";
        const words = text.split(" ");
        const wordEls: HTMLSpanElement[] = [];
        words.forEach((word, i) => {
          const span = document.createElement("span");
          span.style.display = "inline-block";
          span.textContent = word;
          element.appendChild(span);
          wordEls.push(span);
          if (i < words.length - 1) {
            const space = document.createElement("span");
            space.innerHTML = "&nbsp;";
            space.style.display = "inline-block";
            element.appendChild(space);
          }
        });
        return wordEls;
      }

      function animateSection(idx: number) {
        if (animatedSections.has(idx)) return;
        animatedSections.add(idx);
        const el = document.getElementById(`section-${idx}`);
        if (!el) return;
        switch (idx) {
          case 0: {
            const tl = gsap.timeline();
            const img = el.querySelector(".image-mask-container img");
            tl.fromTo(img, { scale: 1.4, opacity: 0 }, { scale: 1, opacity: 1, duration: 1.5, ease: "power3.out" }, 0);
            const giantText = el.querySelector(".giant-text") as HTMLElement;
            if (giantText) { const chars = splitTextToChars(giantText); tl.fromTo(chars, { opacity: 0, y: 60, rotateX: -90 }, { opacity: 1, y: 0, rotateX: 0, stagger: 0.03, duration: 0.8, ease: "power3.out" }, 0.3); }
            const overlapImg = el.querySelector(".small-overlap-img");
            if (overlapImg) tl.fromTo(overlapImg, { opacity: 1, scale: 0.8, yPercent: 400 }, { opacity: 1, scale: 1, yPercent: 0, duration: 1, ease: "power2.out" }, 0.8);
            const subDesc = el.querySelector(".sub-desc");
            if (subDesc) tl.fromTo(subDesc, { clipPath: "inset(0 100% 0 0)", opacity: 0 }, { clipPath: "inset(0 0% 0 0)", opacity: 1, duration: 1.2, ease: "power2.inOut" }, 1);
            break;
          }
          case 1: {
            const tl = gsap.timeline();
            const st = el.querySelector(".section-title");
            if (st) tl.fromTo(st, { clipPath: "inset(100% 0 0 0)", opacity: 0 }, { clipPath: "inset(0% 0 0 0)", opacity: 1, duration: 1, ease: "power3.out" }, 0);
            const imgFrame = el.querySelector(".image-card-frame");
            if (imgFrame) { tl.fromTo(imgFrame, { clipPath: "inset(0 0 100% 0)" }, { clipPath: "inset(0 0 0% 0)", duration: 1.2, ease: "power2.out" }, 0.3); const fi = imgFrame.querySelector("img"); if (fi) tl.fromTo(fi, { scale: 1.3 }, { scale: 1, duration: 2, ease: "power1.out" }, 0.3); }
            const cat = el.querySelector(".cat-label");
            if (cat) tl.fromTo(cat, { opacity: 0, x: -20 }, { opacity: 1, x: 0, duration: 0.6, ease: "power2.out" }, 0.5);
            const bt = el.querySelector(".body-title-large");
            if (bt) tl.fromTo(bt, { clipPath: "inset(0 0 100% 0)", opacity: 0 }, { clipPath: "inset(0 0 0% 0)", opacity: 1, duration: 1.2, ease: "power3.out" }, 0.7);
            const cols = el.querySelectorAll(".engine-columns p");
            if (cols.length) tl.fromTo(cols, { opacity: 0, y: 25 }, { opacity: 1, y: 0, stagger: 0.2, duration: 0.8, ease: "power2.out" }, 1.2);
            break;
          }
          case 2: {
            const tl = gsap.timeline();
            const ot = el.querySelector(".giant-text-outline");
            if (ot) tl.fromTo(ot, { clipPath: "inset(0 100% 0 0)", opacity: 0 }, { clipPath: "inset(0 0% 0 0)", opacity: 1, duration: 1.5, ease: "power2.inOut" }, 0);
            const banner = el.querySelector(".we-move-banner");
            if (banner) { tl.fromTo(banner, { clipPath: "inset(50% 0 50% 0)", opacity: 0 }, { clipPath: "inset(0% 0 0% 0)", opacity: 1, duration: 1.2, ease: "power3.out" }, 0.6); const bi = banner.querySelector("img"); if (bi) tl.fromTo(bi, { scale: 1.2 }, { scale: 1, duration: 3, ease: "sine.out" }, 0.6); }
            break;
          }
          case 3: {
            const tl = gsap.timeline();
            el.querySelectorAll(".point-item").forEach((item, i) => {
              const d = i * 0.35;
              const num = item.querySelector(".point-num"); if (num) tl.fromTo(num, { opacity: 0, x: -15 }, { opacity: 1, x: 0, duration: 0.5, ease: "power2.out" }, d);
              const h3 = item.querySelector("h3"); if (h3) tl.fromTo(h3, { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 0.6, ease: "power2.out" }, d + 0.1);
              const p = item.querySelector("p"); if (p) tl.fromTo(p, { opacity: 0, y: 15 }, { opacity: 1, y: 0, duration: 0.6, ease: "power2.out" }, d + 0.2);
              const img = item.querySelector(".point-img"); if (img) tl.fromTo(img, { clipPath: "inset(100% 0 0 0)" }, { clipPath: "inset(0% 0 0 0)", duration: 1, ease: "power2.out" }, d + 0.3);
            });
            break;
          }
          case 4: {
            const tl = gsap.timeline();
            const bgImg = el.querySelector(".banner-full-bg img");
            if (bgImg) tl.fromTo(bgImg, { scale: 1.3, opacity: 0 }, { scale: 1, opacity: 1, duration: 2.5, ease: "power1.out" }, 0);
            const h1 = el.querySelector(".banner-content h1") as HTMLElement;
            if (h1) { const words = splitTextToWords(h1); tl.fromTo(words, { opacity: 0, filter: "blur(10px)", y: 30 }, { opacity: 1, filter: "blur(0px)", y: 0, stagger: 0.08, duration: 0.9, ease: "power2.out" }, 0.5); }
            const sub = el.querySelector(".sub-banner-title");
            if (sub) tl.fromTo(sub, { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 1, ease: "power2.out" }, 1.2);
            break;
          }
          case 5: {
            const tl = gsap.timeline();
            const hl = el.querySelector(".giant-headline") as HTMLElement;
            if (hl) { const chars = splitTextToChars(hl); tl.fromTo(chars, { opacity: 0, rotateY: 90, x: -10 }, { opacity: 1, rotateY: 0, x: 0, stagger: 0.025, duration: 0.7, ease: "power3.out" }, 0); }
            const portrait = el.querySelector(".image-frame-portrait");
            if (portrait) { tl.fromTo(portrait, { clipPath: "inset(0 0 0 100%)" }, { clipPath: "inset(0 0 0 0%)", duration: 1.4, ease: "power2.inOut" }, 0.3); const pi = portrait.querySelector("img"); if (pi) tl.fromTo(pi, { scale: 1.2 }, { scale: 1, duration: 2.5, ease: "sine.out" }, 0.3); }
            const tt = el.querySelector(".timeless-text") as HTMLElement;
            if (tt) { const chars2 = splitTextToChars(tt); tl.fromTo(chars2, { opacity: 0, y: 12 }, { opacity: 1, y: 0, stagger: 0.015, duration: 0.5, ease: "power2.out" }, 0.8); }
            break;
          }
          case 6: {
            const tl = gsap.timeline();
            const subtitle = el.querySelector(".outro-subtitle");
            if (subtitle) tl.fromTo(subtitle, { opacity: 0, letterSpacing: "1em" }, { opacity: 1, letterSpacing: "0.5em", duration: 1.5, ease: "power2.out" }, 0);
            const title = el.querySelector(".outro-title");
            if (title) tl.fromTo(title, { opacity: 0, scale: 0.7, filter: "blur(8px)" }, { opacity: 1, scale: 1, filter: "blur(0px)", duration: 1.8, ease: "power2.out" }, 0.4);
            const line = el.querySelector(".signature-line");
            if (line) tl.fromTo(line, { width: 0 }, { width: 80, duration: 1.2, ease: "power2.inOut" }, 1.5);
            break;
          }
        }
      }

      playPauseBtn.onclick = () => {
        if (wavesurfer.isPlaying()) {
          wavesurfer.pause();
          playIcon.classList.remove("hidden");
          pauseIcon.classList.add("hidden");
        } else {
          wavesurfer.play();
          playIcon.classList.add("hidden");
          pauseIcon.classList.remove("hidden");
        }
      };

      setTimeout(() => {
        mainContainerRef.current?.classList.add("visible");
        wavesurfer.play();
        playIcon.classList.add("hidden");
        pauseIcon.classList.remove("hidden");
      }, 800);

      // ── THREE.JS ──
      const { default: THREE } = await import("three");
      const { EffectComposer } = await import("three/examples/jsm/postprocessing/EffectComposer.js");
      const { RenderPass } = await import("three/examples/jsm/postprocessing/RenderPass.js");
      const { UnrealBloomPass } = await import("three/examples/jsm/postprocessing/UnrealBloomPass.js");
      const { GLTFLoader } = await import("three/examples/jsm/loaders/GLTFLoader.js");
      const { DRACOLoader } = await import("three/examples/jsm/loaders/DRACOLoader.js");

      const canvas = threeCanvasRef.current!;
      const scene = new THREE.Scene();
      scene.background = null;
      scene.fog = new THREE.Fog(0x020617, 10, 60);

      const camera = new THREE.PerspectiveCamera(60, innerWidth / innerHeight, 0.1, 100);
      camera.position.set(0, 3, 10);

      const renderer3 = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
      renderer3.setSize(innerWidth, innerHeight);
      renderer3.setPixelRatio(Math.min(devicePixelRatio, 2));
      renderer3.outputColorSpace = THREE.SRGBColorSpace;
      renderer3.toneMapping = THREE.ACESFilmicToneMapping;
      renderer3.toneMappingExposure = 1.2;

      const composer3 = new EffectComposer(renderer3);
      composer3.addPass(new RenderPass(scene, camera));
      const bloomPass = new UnrealBloomPass(new THREE.Vector2(innerWidth, innerHeight), 1.5, 0.6, 0.1);
      composer3.addPass(bloomPass);

      const ambient = new THREE.AmbientLight(0x555353, 0.2);
      scene.add(ambient);
      const point = new THREE.PointLight(0xffffff, 10, 100);
      point.position.set(-0.425, 0, 0);
      scene.add(point);

      const ToonLight = new THREE.RectAreaLight(0xf1fff4, 6, 10, 10);
      ToonLight.position.set(0, 2, -5.06);
      scene.add(ToonLight);

      const spotLight = new THREE.SpotLight(0x2b1fc7, 16, 22, 0.3, 0.61, 0.1);
      spotLight.position.set(-5.2, 19.1, -4.92);
      scene.add(spotLight);

      const dirLight = new THREE.DirectionalLight(0xb11f1f, 2);
      dirLight.position.set(5, 10, 7);
      scene.add(dirLight);

      const dracoLoader = new DRACOLoader();
      dracoLoader.setDecoderPath("https://www.gstatic.com/draco/versioned/decoders/1.5.7/");
      const loader = new GLTFLoader();
      loader.setDRACOLoader(dracoLoader);

      loader.load(
        "/models/assets/ekko_main.glb",
        (gltf: any) => {
          const model = gltf.scene;
          model.scale.set(20, 20, 20);
          model.rotation.set(0, -Math.PI / 2, 0);
          model.position.z = -5;
          model.traverse((mesh: any) => { if (mesh.isMesh) { mesh.castShadow = true; mesh.receiveShadow = true; } });
          scene.add(model);
        },
        undefined,
        (err: any) => console.error(err)
      );

      let animReq: number;
      const animate3 = () => { animReq = requestAnimationFrame(animate3); composer3.render(); };
      animate3();

      window.addEventListener("resize", () => {
        camera.aspect = innerWidth / innerHeight;
        camera.updateProjectionMatrix();
        renderer3.setSize(innerWidth, innerHeight);
        composer3.setSize(innerWidth, innerHeight);
      });

      return () => { cancelAnimationFrame(animReq); renderer3.dispose(); };
    })();
  }, [hasStarted]);

  const handleStart = () => {
    const intro = document.getElementById("intro-screen");
    if (intro) intro.classList.add("hidden");
    const mc = mainContainerRef.current;
    if (mc) { mc.classList.remove("hidden"); }
    setHasStarted(true);
  };

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: css }} />

      <div id="slideshow-bg" className="slideshow-bg" />

      <div className="glow glow-1" />
      <div className="glow glow-2" />

      <div id="intro-screen" className="overlay">
        <div className="intro-content">
          <h2 className="sub-title">A CINEMATIC JOURNEY</h2>
          <h1 className="main-title">LUXURY REDEFINED</h1>
          <p className="intro-desc">Put on your headphones for the best experience.</p>
          <button id="start" className="btn-primary" onClick={handleStart}>
            <span>Enter Experience</span>
            <div className="btn-glow" />
          </button>
        </div>
      </div>

      <div ref={mainContainerRef} className="main-container hidden">
        <header className="header-fixed">
          <span className="logo">LUXURY REDEFINED</span>
          <span className="status">PLAYING EXPERIENCE</span>
        </header>

        <section id="section-0" className="editorial-section sec-split">
          <div className="sec-left">
            <div className="image-mask-container scale-reveal">
              <img src="https://images.unsplash.com/photo-1547996160-81dfa63595aa?q=80&w=600" alt="luxury watch" />
            </div>
          </div>
          <div className="sec-right">
            <div className="title-overlap">
              <h1 className="giant-text stagger-text">FOR<br />CENTURIES</h1>
              <div className="small-overlap-img scale-reveal">
                <img src="https://images.unsplash.com/photo-1507679799987-c73779587ccf?q=80&w=400" alt="details" />
              </div>
            </div>
            <p className="sub-desc clip-left-right">greatness has never announced itself.</p>
          </div>
        </section>

        <section id="section-1" className="editorial-section sec-engine">
          <div style={{ fontSize: "20vw", top: 0, filter: "blur(10px)", lineHeight: "50%", paddingTop: "5rem", width: "100%", height: "fit-content", position: "absolute", color: "bisque" }}>
            I Dint neeed TO
          </div>
          <div className="engine-left">
            <h2 className="section-title slide-reveal">The engine of every show.</h2>
            <div className="image-card-frame clip-reveal">
              <img src="https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?q=80&w=600" alt="minimal wave" />
            </div>
          </div>
          <div className="engine-right">
            <span className="cat-label">What We Do</span>
            <h1 className="body-title-large clip-lines">We don't just move gear. We make sure everything gets where it needs to be, when it matters most.</h1>
            <div className="engine-columns">
              <p>Every detail is handled. Every deadline met. No stress. No scrambling. Just a team that makes sure everything rolls exactly as planned.</p>
              <p>From the road to the stage, safety is at the heart of everything we do. Inspected vehicles, strict protocols, experienced crew.</p>
            </div>
          </div>
          <div style={{ fontSize: "20vw", bottom: "5%", lineHeight: "50%", paddingBottom: "10rem", width: "100%", height: "fit-content", position: "absolute", color: "bisque" }}>
            It was recognized.
          </div>
          <div className="engine-left" />
        </section>

        <section id="section-2" className="editorial-section sec-we-move">
          <div className="we-move-container">
            <h1 className="giant-text-outline stroke-reveal">WE MOVE</h1>
            <div className="we-move-banner scale-reveal">
              <img src="https://images.unsplash.com/photo-1558591710-4b4a1ae0f04d?q=80&w=1000" alt="dark abstract banner" />
            </div>
          </div>
          <div style={{ position: "absolute", width: "fit-content", height: "max-content", fontSize: "10rem" }}>
            In the details.
          </div>
        </section>

        <section id="section-3" className="editorial-section sec-grid-points">
          <div className="grid-points-container">
            <div className="point-item fade-in-item">
              <div className="point-num">(1)</div>
              <h3>Flawless Execution</h3>
              <p>In the pursuit of perfection. Every detail is handled. Every deadline met.</p>
              <div className="point-img"><img src="https://images.unsplash.com/photo-1511556532299-8f662fc26c06?q=80&w=300" alt="perfection" /></div>
            </div>
            <div className="point-item fade-in-item">
              <div className="point-num">(2)</div>
              <h3>Safety-first standards</h3>
              <p>Inspected vehicles, strict protocols, experienced crew. We don't gamble with the show.</p>
              <div className="point-img"><img src="https://images.unsplash.com/photo-1581092160607-ee22621dd758?q=80&w=300" alt="safety" /></div>
            </div>
            <div className="point-item fade-in-item">
              <div className="point-num">(3)</div>
              <h3>Solution oriented</h3>
              <p>We sync with your timeline. Today... that legacy continues refined.</p>
              <div className="point-img"><img src="https://images.unsplash.com/photo-1518770660439-4636190af475?q=80&w=300" alt="solutions" /></div>
            </div>
          </div>
        </section>

        <section id="section-4" className="editorial-section sec-banner-full">
          <div className="banner-full-bg">
            <img src="https://images.unsplash.com/photo-1506157786151-b8491531f063?q=80&w=1200" alt="banner bg" />
          </div>
          <div className="banner-content">
            <h1 className="stagger-words">THAT LEGACY CONTINUES.</h1>
            <h2 className="sub-banner-title">MORE REFINED.</h2>
          </div>
        </section>

        <section id="section-5" className="editorial-section sec-timeless">
          <div className="timeless-left">
            <h1 className="giant-headline scale-char">MORE TIMELESS.</h1>
          </div>
          <div className="timeless-right">
            <div className="image-frame-portrait clip-reveal">
              <img src="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?q=80&w=500" alt="modern luxury interior" />
            </div>
            <h3 className="timeless-text stagger-chars">More extraordinary than ever before.</h3>
          </div>
        </section>

        <section id="section-6" className="editorial-section sec-outro">
          <canvas ref={threeCanvasRef} className="three-js" />
          <div className="outro-content">
            <h2 className="outro-subtitle line-reveal">THIS IS</h2>
            <h1 className="outro-title zoom-text">LUXURY REDEFINED</h1>
            <div className="signature-line" />
          </div>
        </section>

        <div className="player-card sticky-player">
          <div className="wave-container">
            <div ref={waveformRef} id="waveform" />
          </div>
          <div className="player-controls">
            <button id="play-pause" className="control-btn" aria-label="Play or Pause">
              <svg className="play-icon" viewBox="0 0 24 24">
                <path fill="currentColor" d="M8 5v14l11-7z" />
              </svg>
              <svg className="pause-icon hidden" viewBox="0 0 24 24">
                <path fill="currentColor" d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
              </svg>
            </button>
            <div className="time-display">
              <span id="time-current">0:00</span> / <span id="time-duration">0:00</span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
